import type { Brand } from '@zg-rentals/rental-manager-header-navigation';
import Urls from './UrlConstants';

const LISTING_ALIAS_REGEX = '(\\w*[a-zA-Z-][a-zA-Z0-9-]*\\w*|\\d+)';

const ZILLOW_RENTAL_MANAGER = 'Zillow Rental Manager';

const StringConstants = {
  APP_NAME: 'satellite-landlord-web',
  INBOX_PAGE_TITLE: `Messages | ${ZILLOW_RENTAL_MANAGER}`,
  MULTIFAMILY_LEADS_PAGE_TITLE: `Leads | ${ZILLOW_RENTAL_MANAGER}`,
  LEAD_MANAGEMENT_PAGE_TITLE: `Find a renter | ${ZILLOW_RENTAL_MANAGER}`,
  NOT_FOUND_PAGE_TITLE: `Page Not Found | ${ZILLOW_RENTAL_MANAGER}`,
  //http method types
  GET: 'get',
  PUT: 'put',
  POST: 'post',
  DELETE: 'delete',

  // direction
  NEXT: 'next',
  PREV: 'prev',

  FORMATTED_BRAND_NAMES: {
    zillow: 'Zillow',
  },

  // using the plain brand names for hotpads and trulia for now whenever we need to use ZRM brand for zillow
  FORMATTED_BRAND_NAMES_RENTAL: {
    zillow: ZILLOW_RENTAL_MANAGER,
  },

  UNFORMATTED_BRAND_NAMES: {
    zillow: 'zillow' as Brand,
  },

  GENERIC_ERROR: 'An error has occurred. Please refresh this page to try again.',

  // marketing
  MARKETING_PATH: {
    PAYMENTS: 'payments',
    APPLICATIONS: 'applications',
  },

  ALLOW_PARAMS_REGEX: '([?].*)?([&].*)?',

  // server side error string identifier
  // only for BE status code > 399 that are to be fixed in near future
  SS_ERRORS: {
    confirm_phone_number: 'Please confirm the contact phone number.',
  },

  SYSTEM_MESSAGE_ACTION: {
    applicationInvitation: 'View Application',
    newApplicationRequest: 'Send Application',
    resendApplication: 'Resend Application',
  },

  CONVERSATION_PAGE_VIEW_TYPE: {
    ALL: 'all', // Unused for now as this will be default
    PROPERTY: 'property',
  },

  GRAPHQL_ERROR_TYPES: {
    AUTHENTICATION: 'authentication_error',
    AUTHORIZATION: 'authorization_error',
    BAD_REQUEST: 'bad_request_error',
    DEFAULT: 'api_error',
    INTERNAL_SERVER: 'internal_server_error',
    NETWORK: 'network_error',
    RESOURCE_NOT_FOUND: 'resource_not_found',
    UNAUTHENTICATED: 'UNAUTHENTICATED',
  },

  REGEX: {
    // listingAlias to have at least 1 letter
    // listingAlias can either be the feedlistingAlias format ie. 12345_Postlets
    // or aliasEncoded ie. 4z9huhyrzuuru
    // There is an astronomically low probability that aliasEncoded will not contain a non-digit character
    // Probabilty = 13^10 / 13 ^ 33
    // https://stackoverflow.com/questions/33965824/regex-to-match-a-word-with-at-least-one-letter-and-any-number-of-digits-no-look/33965869
    // needs to work with single and double dashes ex: freecr-default-308_LCS, 256341-4957_Buildium
    INBOX_PAGE_PATHNAME: `/:listingAlias${LISTING_ALIAS_REGEX}/:conversationId(\\d+)`,
    LEADS_PAGE_PATHNAME: `/:listingAlias${LISTING_ALIAS_REGEX}?`,
    LISTING_ALIAS_PATHNAME: `/:listingAlias${LISTING_ALIAS_REGEX}?`,
    INQUIRY_ID_PATHNAME: '/:inquiryId(\\d+)',
  },

  TOOLTIP: {
    IMPERSONATION: 'Disabled while impersonating',
    IMPERSONATION_VIEW: 'Cannot view while impersonating',
    REPORTED: 'Cannot reply to conversation that has been reported.',
    PHONE_LEAD: 'To reply to this renter, please call or text their number directly.',
    INVITE_TO_APPLY_MOVE_TO_INBOX: 'Move conversation to inbox in order to send invitation.',
    BLOCKED_BY_ZILLOW: 'Cannot reply to a conversation that has been blocked by Zillow.',
    ENTER_TO_SEND_MESSAGE: 'Send message (Enter/Return)',
  },

  CONVERSATION_BLOCKED_BY_ZILLOW_TEXT:
    'Zillow has blocked this conversation because it was determined to be in violation of our Good Neighbor Policy.',
  CONVERSATION_BLOCKED_BY_ZILLOW_LINK: `${Urls.GOOD_NEIGHBOR_POLICY}`,
  UNABLE_LOAD_MESSAGE_TEXT: `Sorry, we're having issues and couldn't load this message.\n We apologize for the inconvenience`,
  DATE_FNS_FORMAT: 'h:mm a',
  FAILED_SEND_TEXT: 'This message failed to send.',
  FAILED_SEND_LONG_MESSAGE_TEXT:
    'This message was too long and failed to send. Try splitting it into smaller messages.',
  SENDING_SEND_TEXT: 'Sending...',

  SCREENING_CRITERIA_PROMPT:
    "When you set screening criteria, these details are added to your listing, which helps renters understand if they’d be a good fit at your property. When they share their income, credit score, or pet details on their profile, we'll display how this compares to your criteria.",

  // Filters and Folders
  ACTIONS: {
    ARCHIVE: 'archive',
    SPAM: 'spam',
    UNARCHIVE: 'unarchive',
    UNSPAM: 'unspam',
    MARK_UNREAD: 'Mark unread',
    NOT_SPAM: 'Not spam',
    MOVE_TO_INBOX: 'Move to inbox',
  },

  MOBILE_PLATFORMS: {
    IOS: 'IOS',
    ANDROID: 'Android',
  },

  ATTACHMENTS: {
    IMAGE_FILE_TYPE: 'image', // use with .includes since an image can be image/jpg, image/png, etc.
    VIDEO_FILE_TYPE: 'video', // use with .includes since a video can be .mp4, .mov, etc.
    IMPERSONATION_DETECTED: 'impersonation-detected',
    STATUS_COMPLETE: 'complete',
  },
};

export default StringConstants;
